import {AppUrls} from 'shared/config/model/urls';

import React, {ReactNode} from 'react';
import {Link} from 'react-router-dom';
import {Control, UseFormRegister, useWatch} from 'react-hook-form';
import {useSelector} from 'react-redux';
import cn from 'classnames';
import {t} from '@lingui/core/macro';

import Checkbox from 'shared/ui/Checkbox/Checkbox';
import Envelope from 'shared/ui/icons/Envelope';
import TelegramNotBackground from 'shared/ui/icons/TelegramNotBackground';
import MaxChat from 'shared/ui/icons/MaxChat';
import {useEmailConfirmation, userSelector} from 'entities/user';
import {isTranslate} from 'shared/lib/lang';

import {PALETTE} from 'shared/config/model/palette';

import {countSelected} from '../../lib/notifySelection';
import {getAdditionalEmailLabel, getMessengerChatLabel} from '../../lib/notifyChannelLabel';

import {additionalEmailAPI} from '../../api/additionalEmailAPI';
import {maxChatAPI} from '../../api/maxChatAPI';
import {telegramChatAPI} from '../../api/telegramChatAPI';

import styles from './NotifyChannels.module.scss';

interface INotifyChannelsProps {
    control: Control<any>,
    register: UseFormRegister<any>,
    emailFieldName: string,
    additionalEmailsFieldName: string,
    telegramFieldName: string,
    maxFieldName: string,
}

interface IChannelCardProps {
    icon: ReactNode,
    title: string,
    count: number,
    connected: boolean,
    children: ReactNode,
}

const ChannelCard: React.FC<IChannelCardProps> = ({icon, title, count, connected, children}) => (
    <div className={cn(styles.card, count > 0 && styles.cardActive)}>
        <div className={styles.cardHead}>
            <span className={styles.icon}>{icon}</span>
            <span className={styles.title}>{title}</span>
            {connected && count > 0 && <span className={styles.count}>{count}</span>}
        </div>
        <div className={styles.cardBody}>{children}</div>
    </div>
);

interface IChannelCheckboxListProps {
    items: {id: number, label: string}[],
    fieldName: string,
    register: UseFormRegister<any>,
}

const ChannelCheckboxList: React.FC<IChannelCheckboxListProps> = ({items, fieldName, register}) => (
    <div className={styles.list}>
        {items.map((item) => (
            <Checkbox
                key={item.id}
                textStyle={styles.checkbox}
                value={`${item.id}`}
                {...register(fieldName)}
            >
                {item.label}
            </Checkbox>
        ))}
    </div>
);

const NotConnected: React.FC<{text: string, to?: string, onAction?: () => void}> = ({text, to, onAction}) => (
    <div className={styles.notConnected}>
        <span className={styles.hint}>{text}</span>
        {
            to
                ? <Link className={styles.link} to={to}>{t`Подключить`}</Link>
                : <button type="button" className={styles.link} onClick={onAction}>{t`Подтвердить`}</button>
        }
    </div>
);

const NotifyChannels: React.FC<INotifyChannelsProps> = ({
    control,
    register,
    emailFieldName,
    additionalEmailsFieldName,
    telegramFieldName,
    maxFieldName,
}) => {
    const user = useSelector(userSelector);
    const confirmEmail = useEmailConfirmation();

    const showMax = !isTranslate();

    const {data: telegramChats, isLoading: isLoadingTelegram}
        = telegramChatAPI.useGetActiveQuery();
    const {data: maxChats, isLoading: isLoadingMax}
        = maxChatAPI.useGetActiveQuery(undefined, {skip: !showMax});
    const {data: additionalEmails} = additionalEmailAPI.useGetActiveQuery();

    const notifyByEmail = useWatch({control, name: emailFieldName});
    const selectedEmails = useWatch({control, name: additionalEmailsFieldName});
    const selectedTelegram = useWatch({control, name: telegramFieldName});
    const selectedMax = useWatch({control, name: maxFieldName});

    const emailConnected = !!user?.email_confirmed;
    const telegramConnected = !isLoadingTelegram && !!telegramChats?.length;
    const maxConnected = !isLoadingMax && !!maxChats?.length;

    const emailCount = (notifyByEmail ? 1 : 0) + countSelected(selectedEmails);

    return (
        <div className={styles.container}>
            <ChannelCard
                icon={<Envelope color={PALETTE.accent}/>}
                title="Email"
                count={emailCount}
                connected={emailConnected}
            >
                {emailConnected ? (
                    <div className={styles.list}>
                        <Checkbox textStyle={styles.checkbox} {...register(emailFieldName)}>
                            {user?.email}
                        </Checkbox>
                        {additionalEmails?.map((email) => (
                            <Checkbox
                                key={email.id}
                                textStyle={styles.checkbox}
                                value={`${email.id}`}
                                {...register(additionalEmailsFieldName)}
                            >
                                {getAdditionalEmailLabel(email)}
                            </Checkbox>
                        ))}
                    </div>
                ) : (
                    <NotConnected text={t`E-mail не подтверждён`} onAction={confirmEmail}/>
                )}
            </ChannelCard>

            <ChannelCard
                icon={<TelegramNotBackground/>}
                title="Telegram"
                count={countSelected(selectedTelegram)}
                connected={telegramConnected}
            >
                {telegramConnected && telegramChats ? (
                    <ChannelCheckboxList
                        items={telegramChats.map((chat) => ({id: chat.id, label: getMessengerChatLabel(chat)}))}
                        fieldName={telegramFieldName}
                        register={register}
                    />
                ) : (
                    !isLoadingTelegram && <NotConnected text={t`Telegram не подключён`} to={AppUrls.SETTINGS}/>
                )}
            </ChannelCard>

            {showMax && (
                <ChannelCard
                    icon={<MaxChat color="colored"/>}
                    title="Max"
                    count={countSelected(selectedMax)}
                    connected={maxConnected}
                >
                    {maxConnected && maxChats ? (
                        <ChannelCheckboxList
                            items={maxChats.map((chat) => ({id: chat.id, label: getMessengerChatLabel(chat)}))}
                            fieldName={maxFieldName}
                            register={register}
                        />
                    ) : (
                        !isLoadingMax && <NotConnected text={t`Max не подключён`} to={AppUrls.SETTINGS}/>
                    )}
                </ChannelCard>
            )}
        </div>
    );
};

export default NotifyChannels;
